(() => {
  if (window.__miniCartScriptLoaded) return;
  window.__miniCartScriptLoaded = true;

  const CART_SECTION = 'mini-cart';
  const GIFT_MEMORY_KEY = 'mini-cart-gift';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const routes = () => (window.themeVariables && window.themeVariables.routes) || {};
  const getDrawer = () => document.getElementById('mini-cart');
  const getSheet = () => document.getElementById('mini-cart-sheet');
  const getToast = () => document.querySelector('mini-cart-toast');
  const fill = (text, values) =>
    Object.keys(values).reduce((output, key) => output.split(`[[${key}]]`).join(values[key]), text || '');
  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const money = (cents) => (window.formatThemeMoney ? window.formatThemeMoney(cents) : (cents / 100).toFixed(2));
  const parseJson = (element, fallback) => {
    try {
      return element ? JSON.parse(element.textContent) : fallback;
    } catch (error) {
      return fallback;
    }
  };

  let stringCache = null;
  const decodeEntities = (value) => {
    const area = document.createElement('textarea');
    area.innerHTML = value;
    return area.value;
  };
  const decodeStrings = (table) => {
    Object.keys(table || {}).forEach((key) => {
      if (typeof table[key] === 'string') table[key] = decodeEntities(table[key]);
      else if (table[key] && typeof table[key] === 'object') decodeStrings(table[key]);
    });
    return table;
  };
  const LINE_LENGTH = 120;
  const cleanLine = (text) => {
    let line = String(text || '').replace(/\s+/g, ' ').trim();
    const cut = /(\.{2,}|\u2026)$/.test(line);
    line = line.replace(/\s*(\.{2,}|\u2026)$/, '');
    if (/^[-\u2022]\s/.test(line)) {
      const items = line
        .split(/[-\u2022]\s+/)
        .map((part) => part.trim().replace(/[.!,;:]+$/, ''))
        .filter(Boolean);
      if (cut && items.length > 1) items.pop();
      return items.slice(0, 2).join(' \u00b7 ');
    }
    const parts = line.split(/([.!?])\s+/);
    const sentences = [];
    for (let index = 0; index < parts.length; index += 2) sentences.push(`${parts[index]}${parts[index + 1] || ''}`.trim());
    if (cut && sentences.length > 1) sentences.pop();
    line = sentences
      .filter((sentence) => {
        const compact = sentence.replace(/\s+!$/, '!');
        return compact && !(compact.split(/\s+/).length <= 2 && /!$/.test(compact));
      })
      .filter((sentence, index) => index === 0 || !/^(jetzt\b|entdeck|bestell|discover\b|shop now|order now|buy now)/i.test(sentence))
      .slice(0, 2)
      .reduce((kept, sentence) => (!kept || kept.length + sentence.length < LINE_LENGTH ? `${kept} ${sentence}`.trim() : kept), '');
    return line.replace(/\s+[\u2013\u2014]\s+(\S+\s+)?\S+[.!]?\s*$/, '.');
  };
  const strings = () => {
    if (!stringCache) stringCache = decodeStrings(parseJson(document.querySelector('[data-mini-cart-strings]'), {}));
    return stringCache;
  };
  const readState = () => parseJson(getDrawer()?.querySelector('[data-mini-cart-state]'), {});

  let knownItemCount = null;
  let latestCart = null;

  const dispatchCart = (cart, detail = {}) => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, ...detail } }));
  };

  const postJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(json.description || json.message || 'Cart request failed'), { json });
    return json;
  };

  const fetchCart = async () => {
    const response = await fetch(`${routes().cartUrl}.js`, { credentials: 'same-origin' });
    latestCart = await response.json();
    return latestCart;
  };

  const addItems = async (items) => {
    const added = await postJson(`${routes().cartAddUrl}.js`, { items, sections: [CART_SECTION] });
    const cart = await fetchCart();
    cart.sections = added.sections;
    return { added, cart };
  };

  const changeLine = (key, quantity) =>
    postJson(`${routes().cartChangeUrl}.js`, { id: key, quantity, sections: [CART_SECTION] });

  const shake = (element) => {
    if (!element || reduceMotion()) return;
    element.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-6px)' },
        { transform: 'translateX(5px)' },
        { transform: 'translateX(-3px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 360, easing: 'ease-out' }
    );
  };

  class MiniCartToast extends HTMLElement {
    show(message, anchor) {
      if (!message) return;
      const drawer = getDrawer();
      const footer = drawer?.querySelector('.mini-cart__foot');
      const body = drawer?.querySelector('[data-mini-cart-body]');
      const holder = document.createElement('div');
      holder.innerHTML = message;
      this.textContent = holder.textContent;
      const sheet = getSheet();
      const region = sheet?.open
        ? sheet.querySelector('[data-mini-cart-announce]')
        : drawer?.open
          ? drawer.querySelector('[data-mini-cart-announce]')
          : document.querySelector('[data-mini-cart-announce="page"]');
      if (region) {
        region.textContent = '';
        setTimeout(() => (region.textContent = holder.textContent), 80);
      }
      this.style.top = '';
      this.style.bottom = `${(drawer?.open && footer ? footer.offsetHeight : 0) + 12}px`;
      if (anchor && anchor.isConnected && body) {
        const lineRect = anchor.getBoundingClientRect();
        const bodyRect = body.getBoundingClientRect();
        this.style.top = `${Math.min(lineRect.bottom + 6, bodyRect.bottom - 50)}px`;
        this.style.bottom = 'auto';
      }
      this.setAttribute('data-visible', '');
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.removeAttribute('data-visible');
        if (region) region.textContent = '';
      }, 2400);
    }
  }

  const toast = (message, anchor) => getToast()?.show(message, anchor);

  const goalMemory = { progress: null, done: getDrawer()?.querySelector('mini-cart-goals')?.dataset.done || '' };

  const burst = (marker) => {
    const rect = marker.getBoundingClientRect();
    const styles = getComputedStyle(marker);
    const colors = [
      styles.getPropertyValue('--mini-cart-accent'),
      styles.getPropertyValue('--mini-cart-ink'),
      `rgb(${styles.getPropertyValue('--accent-men')})`,
      `rgb(${styles.getPropertyValue('--accent-kids')})`,
    ];
    for (let index = 0; index < 18; index++) {
      const piece = document.createElement('span');
      const size = 4 + Math.random() * 4;
      const angle = Math.random() * Math.PI * 2;
      const distance = 36 + Math.random() * 54;
      piece.style.cssText = `position:fixed;left:${rect.left + rect.width / 2}px;top:${rect.top + rect.height / 2}px;width:${size}px;height:${size}px;z-index:1000004;pointer-events:none;background:${colors[index % colors.length]};`;
      document.body.appendChild(piece);
      piece
        .animate(
          [
            { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 },
            {
              transform: `translate(calc(-50% + ${Math.cos(angle) * distance}px), calc(-50% + ${Math.sin(angle) * distance + 16}px)) rotate(${Math.random() * 540 - 270}deg)`,
              opacity: 0,
            },
          ],
          { duration: 620 + Math.random() * 280, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' }
        )
        .finished.then(() => piece.remove(), () => piece.remove());
    }
  };

  class MiniCartGoals extends HTMLElement {
    connectedCallback() {
      this.fillElement = this.querySelector('[data-goal-fill]');
      this.progress = Number(this.dataset.progress) || 0;
      this.done = this.dataset.done || '';
      if (getDrawer()?.open) this.play(goalMemory.progress ?? this.progress);
    }

    play(from) {
      if (this.fillElement && !reduceMotion()) {
        this.fillElement.style.transition = 'none';
        this.fillElement.style.width = `${from}%`;
        this.fillElement.getBoundingClientRect();
        this.fillElement.style.transition = '';
        this.fillElement.style.width = `${this.progress}%`;
      }
      const before = goalMemory.done.split(',');
      this.done
        .split(',')
        .filter((goal) => goal && !before.includes(goal))
        .slice(-1)
        .forEach((goal) => this.celebrate(goal, from === 0 ? 760 : 120));
      goalMemory.progress = this.progress;
      goalMemory.done = this.done;
    }

    celebrate(goal, delay) {
      if (reduceMotion()) return;
      setTimeout(() => {
        const marker = getDrawer()?.querySelector(`mini-cart-goals [data-goal-marker="${goal}"]`);
        if (!marker) return;
        marker.animate([{ scale: '1' }, { scale: '1.25' }, { scale: '1' }], { duration: 420, easing: 'cubic-bezier(.34,1.56,.64,1)' });
        burst(marker);
      }, delay);
    }
  }

  let addSource = null;
  let pendingAdd = null;
  let returnFocus = null;

  const rememberAddSource = (event) => {
    const button = event.target.closest('button');
    const form = button && (button.form || button.closest('form'));
    if (form && form.matches('form[action*="/cart/add"]')) addSource = button;
  };

  const rememberPendingAdd = (event) => {
    const item = event.detail && event.detail.variant;
    if (!item) return;
    const fromCart = event.target instanceof Element && event.target.closest('#mini-cart, mini-cart-sheet');
    const source = fromCart ? null : addSource;
    const items = Array.isArray(event.detail.items) && event.detail.items.length ? event.detail.items : [item];
    pendingAdd = {
      variantIds: [...new Set(items.map((entry) => String(entry.variant_id || entry.id)))],
      source,
      sourceRect: source ? source.getBoundingClientRect() : null,
      wasEmpty: knownItemCount === 0,
      at: Date.now(),
    };
    addSource = null;
  };

  const flashLine = (line, scroll = true) => {
    const body = line.closest('[data-mini-cart-body]');
    let scrolled = false;
    if (body && scroll) {
      const top = line.offsetTop - 12;
      if (top < body.scrollTop || top + line.offsetHeight > body.scrollTop + body.clientHeight) {
        body.scrollTo({ top, behavior: reduceMotion() ? 'auto' : 'smooth' });
        scrolled = true;
      }
    }
    if (!reduceMotion()) {
      const accent = getComputedStyle(line).getPropertyValue('--mini-cart-accent').trim();
      line.animate([{ backgroundColor: accent || 'transparent' }, { backgroundColor: 'transparent' }], {
        duration: 1100,
        easing: 'cubic-bezier(.23,1,.32,1)',
      });
    }
    return scrolled;
  };

  const land = (line, add, delay, last = true, scroll = true) => {
    setTimeout(() => {
      if (!line.isConnected) return;
      const scrolled = flashLine(line, scroll);
      if (add.wasEmpty && last) setTimeout(() => toast(strings().toastAdded, line), scrolled ? 420 : 60);
    }, delay);
  };

  const fly = (add, line, index, last) => {
    const target = line.querySelector('.mini-cart-line__image');
    const scroll = index === 0;
    if (!target || reduceMotion()) {
      land(line, add, 500 + index * 80, last, scroll);
      return;
    }
    target.style.opacity = '0';
    setTimeout(() => {
      const from = add.sourceRect || add.source.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      if (!from.width || !to.width) {
        target.style.opacity = '';
        land(line, add, 0, last, scroll);
        return;
      }
      const size = 96;
      const x0 = from.left + from.width / 2 - size / 2;
      const y0 = from.top + from.height / 2 - size / 2;
      const flyer = document.createElement('img');
      flyer.className = 'mini-cart-fly';
      flyer.alt = '';
      flyer.src = target.currentSrc || target.src;
      flyer.style.cssText = `left:${x0}px;top:${y0}px;width:${size}px;height:${size}px`;
      document.body.appendChild(flyer);
      const dx = to.left + to.width / 2 - (x0 + size / 2);
      const dy = to.top + to.height / 2 - (y0 + size / 2);
      const scale = to.width / size;
      const animation = flyer.animate(
        [
          { transform: 'translate(0, 0) scale(0.9)' },
          { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 90}px) scale(${(1 + scale) / 2})`, offset: 0.5 },
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
        ],
        { duration: 640, easing: 'cubic-bezier(.5,0,.2,1)', fill: 'forwards' }
      );
      animation.onfinish = () => {
        target.style.opacity = '';
        flyer.remove();
        if (!line.isConnected) return;
        flashLine(line, scroll);
        if (add.wasEmpty && last) toast(strings().toastAdded, line);
      };
    }, 470 + index * 80);
  };

  const secretPrice = (decimal) => {
    const value = Number(decimal);
    if (!(value > 0) || !window.PresentmentMoney) return null;
    const converted = window.PresentmentMoney.convert(value);
    const presentment = window.PresentmentMoney.rate() === 1 ? converted : Math.floor(converted) + 0.99;
    return Math.round(presentment * 100);
  };

  const applyCardPrices = (root) => {
    if (!root) return;
    const secretActive = Boolean(window.SecretSaleSession?.isActive?.());
    const influencer = !secretActive && window.InfluencerPromo?.state;
    root.querySelectorAll('[data-card-price]').forEach((element) => {
      if (element.dataset.baseHtml === undefined) element.dataset.baseHtml = element.innerHTML;
      const price = Number(element.dataset.priceCents);
      const compare = Number(element.dataset.compareCents || 0);
      const reference = compare > price ? compare : price;
      const from = element.hasAttribute('data-price-from');
      let current = null;
      if (secretActive) {
        const secret = secretPrice(element.dataset.secretPrice);
        if (secret !== null && secret < price) current = secret;
      } else if (influencer && influencer.percent && price > 0) {
        current = window.InfluencerPromo.cut(price, influencer.percent);
      }
      if (current === null) {
        element.innerHTML = element.dataset.baseHtml;
        return;
      }
      const label = from ? fill(strings().priceFrom, { price: money(current) }) : money(current);
      element.innerHTML = `<s>${money(reference)}</s>${label}`;
    });
  };

  const onInfluencerChanged = () => {
    applyCardPrices(getDrawer());
    applyCardPrices(getSheet());
  };

  const saleRow = {
    cards: null,
    loading: null,

    load(url) {
      if (!url) return Promise.resolve();
      if (!this.loading) {
        this.loading = fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
          .then((html) => {
            const holder = document.createElement('div');
            holder.innerHTML = html;
            this.cards = Array.from(holder.querySelectorAll('[data-sale-cards] > li'));
          })
          .catch(() => {
            this.loading = null;
          });
      }
      return this.loading;
    },

    prefetch() {
      this.load(getDrawer()?.querySelector('[data-sale-slot]')?.dataset.url);
    },

    async fill(drawer) {
      const slot = drawer?.querySelector('[data-sale-slot]');
      if (!slot) return;
      await this.load(slot.dataset.url);
      if (!this.cards || !slot.isConnected) return;
      const first = (slot.dataset.first || '').split(',').filter(Boolean);
      const exclude = slot.dataset.exclude || '';
      const rank = (card) => {
        const index = first.indexOf(card.dataset.productId);
        if (index >= 0) return [0, index];
        const audience = card.dataset.audience;
        return [audience === slot.dataset.audience ? 1 : audience === 'kids' ? 2 : 3, 100 - Number(card.dataset.discount || 0)];
      };
      const cards = this.cards
        .filter((card) => !exclude.includes(`,${card.dataset.productId},`))
        .map((card, index) => ({ card, index, key: rank(card) }))
        .sort((a, b) => a.key[0] - b.key[0] || a.key[1] - b.key[1] || a.index - b.index)
        .slice(0, 30)
        .map(({ card }) => card.cloneNode(true));
      slot.querySelector('.mini-cart-sale__track')?.replaceChildren(...cards);
      slot.hidden = cards.length === 0;
      applyCardPrices(slot);
    },
  };

  const prefetchSaleRowOnIntent = (event) => {
    if (!saleRow.loading && event.target.closest?.('[aria-controls="mini-cart"]')) saleRow.prefetch();
  };
  const prefetchSaleRowAfterAdd = () => {
    if (!window.location.pathname.includes('/collections')) saleRow.prefetch();
  };

  const cartWork = new Set();
  const trackCartWork = (promise) => {
    const settled = promise.then(
      () => {},
      () => {}
    );
    cartWork.add(settled);
    settled.then(() => cartWork.delete(settled));
    return promise;
  };

  let cartLock = Promise.resolve();
  const withCartLock = (task) => {
    const result = cartLock.then(task);
    cartLock = result.catch(() => {});
    return trackCartWork(result);
  };

  const gift = {
    queued: false,
    again: false,
    dispatching: false,

    memory(cart) {
      let memo = {};
      try {
        memo = JSON.parse(localStorage.getItem(GIFT_MEMORY_KEY) || '{}');
      } catch (error) {
        memo = {};
      }
      return memo.token === cart.token ? memo : { token: cart.token };
    },

    save(memo) {
      try {
        localStorage.setItem(GIFT_MEMORY_KEY, JSON.stringify(memo));
      } catch (error) {}
    },

    qualifying(cart, rule, isGift) {
      const regular = cart.items.filter((item) => !isGift(item));
      if (rule.type !== 'SUBTOTAL') return regular.reduce((total, item) => total + item.quantity, 0);
      const regularTotal = regular.reduce((total, item) => total + item.final_line_price, 0);
      const cartTotal = cart.items.reduce((total, item) => total + item.final_line_price, 0);
      const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
        (total, application) => total + (application.total_allocated_amount || 0),
        0
      );
      const share = cartTotal > 0 ? regularTotal / cartTotal : 0;
      return Math.max(regularTotal - cartDiscount * share, 0);
    },

    remember(cart, rule, variantId) {
      const memo = this.memory(cart);
      memo.added = true;
      memo.productId = rule ? rule.productId : null;
      memo.variantId = variantId;
      this.save(memo);
    },

    dispatch(cart) {
      this.dispatching = true;
      try {
        dispatchCart(cart, { replacementDelay: 0 });
      } finally {
        this.dispatching = false;
      }
    },

    threshold(rule) {
      return rule.type === 'SUBTOTAL' && window.PresentmentMoney ? window.PresentmentMoney.convertCents(rule.threshold) : rule.threshold;
    },

    mismatch(state) {
      const rule = state.gift;
      if (!rule || !state.itemCount) return false;
      const progress = rule.type === 'SUBTOTAL' ? state.nonGiftTotal : state.nonGiftCount;
      const gifts = Number(state.giftQuantity) || 0;
      if (progress < this.threshold(rule)) return gifts > 0;
      return gifts > 1 || (gifts === 0 && Boolean(rule.available));
    },

    refresh() {
      return trackCartWork(
        fetchCart()
          .then((cart) => this.sync(cart))
          .catch(() => false)
      );
    },

    action(cart, rule) {
      const isGift = (item) => item.product_id === rule.productId && item.properties && item.properties._free_gift_product === 'true';
      const gifts = cart.items.filter(isGift);
      const threshold = this.threshold(rule);
      const memo = this.memory(cart);
      if (this.qualifying(cart, rule, isGift) < threshold) {
        if (gifts.length) return { remove: gifts[0], memo };
        if (memo.added || memo.declined) {
          memo.added = false;
          delete memo.declined;
          this.save(memo);
        }
        return null;
      }
      if (gifts.length > 1 || (gifts.length && gifts[0].quantity > 1)) {
        return { surplus: gifts.length > 1 ? gifts[1] : gifts[0], keepOne: gifts.length === 1, memo };
      }
      if (gifts.length) {
        if (!memo.added) {
          memo.added = true;
          this.save(memo);
        }
        return null;
      }
      if (memo.declined || !rule.available || !rule.variantId) return null;
      if (!memo.added && memo.failedAt && Date.now() - memo.failedAt < 300000) return null;
      return { add: true, memo };
    },

    async reconcile(rule) {
      const action = this.action(await fetchCart(), rule);
      if (!action) return false;
      const { memo } = action;
      if (action.remove) {
        const updatedCart = await changeLine(action.remove.key, 0);
        memo.added = false;
        this.save(memo);
        this.dispatch(updatedCart);
        return true;
      }
      if (action.surplus) {
        this.dispatch(await changeLine(action.surplus.key, action.keepOne ? 1 : 0));
        return true;
      }
      if (memo.added) {
        memo.added = false;
        memo.declined = true;
        this.save(memo);
        return false;
      }
      const remembered = String(memo.productId) === String(rule.productId) ? memo.variantId : null;
      const candidates = [...new Set([remembered, rule.variantId].filter(Boolean).map(String))];
      for (const variantId of candidates) {
        try {
          const { cart: updatedCart } = await addItems([{ id: Number(variantId), quantity: 1, properties: { _free_gift_product: 'true' } }]);
          memo.added = true;
          delete memo.failedAt;
          this.save(memo);
          this.dispatch(updatedCart);
          return true;
        } catch (error) {}
      }
      memo.failedAt = Date.now();
      this.save(memo);
      return false;
    },

    sync(cart) {
      const rule = readState().gift;
      if (this.dispatching || !rule || !cart || !Array.isArray(cart.items) || !this.action(cart, rule)) return Promise.resolve(false);
      if (this.queued) {
        this.again = true;
        return Promise.resolve(false);
      }
      this.queued = true;
      return withCartLock(() => this.reconcile(rule))
        .catch(() => false)
        .finally(() => {
          this.queued = false;
          if (this.again) {
            this.again = false;
            this.refresh();
          }
        });
    },
  };

  const onCartUpdated = (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart) return;
    knownItemCount = cart.item_count;
    latestCart = cart;
    gift.sync(cart);
  };

  const onDiscountUpdated = (event) => {
    if (event.detail && event.detail.cart) gift.sync(event.detail.cart);
  };

  const analytics = {
    push(event, items, value) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event,
        ecommerce: {
          currency: window.Shopify?.currency?.active || window.themeVariables?.settings?.cartCurrency,
          value,
          items,
        },
      });
    },

    cartItems(cart) {
      const coupon = (cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code).join(',');
      return cart.items.map((item) => ({
        item_id: item.product_id,
        item_variant: item.variant_id,
        item_name: item.product_title,
        price: item.final_price / 100,
        item_brand: item.vendor,
        item_category: item.product_type,
        quantity: item.quantity,
        ...(coupon ? { coupon } : {}),
      }));
    },

    viewCart(cart) {
      try {
        if (cart.item_count > 0) this.push('view_cart', this.cartItems(cart), cart.total_price / 100);
      } catch (error) {}
    },

    lineChange(link) {
      const line = link.closest('[data-analytics-item]');
      if (!line) return;
      const item = parseJson({ textContent: line.dataset.analyticsItem }, null);
      if (!item) return;
      const delta = Number(link.dataset.quantityStep);
      this.push(delta > 0 ? 'add_to_cart' : 'remove_from_cart', [{ ...item, quantity: 1 }], item.price);
    },

    added(item) {
      if (!item) return;
      this.push(
        'add_to_cart',
        [
          {
            item_id: item.product_id,
            item_variant: item.variant_id,
            item_name: item.product_title,
            price: item.final_price / 100,
            item_brand: item.vendor,
            item_category: item.product_type,
            quantity: item.quantity,
          },
        ],
        item.final_line_price / 100
      );
    },

    async trackKlaviyoAdd(added) {
      if (!added) return;
      try {
        const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, { credentials: 'same-origin' });
        const cart = await response.json();
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push([
          'track',
          'Added to Cart',
          {
            $value: cart.total_price / 100,
            total_price: cart.total_price / 100,
            total_discount: cart.total_discount,
            original_total_price: cart.original_total_price / 100,
            items: cart.items,
            AddedItemProductName: added.product_title,
            AddedItemProductID: added.product_id,
            AddedItemVariantID: added.variant_id,
            AddedItemSKU: added.sku,
            AddedItemImageURL: added.image,
            AddedItemURL: `${window.location.origin}${added.url}`,
            AddedItemPrice: added.final_price / 100,
            AddedItemQuantity: added.quantity,
            ItemNames: cart.items.map((item) => item.product_title),
            CheckoutURL: `${window.location.origin}/checkout`,
          },
        ]);
      } catch (error) {}
    },
  };

  const sameProperties = (first, second) => {
    const a = Array.isArray(first) ? Object.fromEntries(first) : first || {};
    const b = Array.isArray(second) ? Object.fromEntries(second) : second || {};
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => String(a[key]) === String(b[key]));
  };

  const rowId = (row) => `${row.dataset.variantId}|${row.dataset.properties || '{}'}`;

  const quantity = {
    pending: new Map(),
    failures: 0,

    step(link) {
      const row = link.closest('line-item[data-group-quantity]');
      if (!row) return;
      const id = rowId(row);
      const entry = this.pending.get(id) || {
        variantId: row.dataset.variantId,
        properties: parseJson({ textContent: row.dataset.properties || '{}' }, {}),
        target: Number(row.dataset.groupQuantity) || 0,
      };
      const delta = Number(link.dataset.quantityStep);
      if (delta > 0 && row.dataset.max !== undefined && entry.target + delta > Number(row.dataset.max)) {
        shake(link.closest('.mini-cart-line__quantity'));
        toast(strings().noMoreStock);
        return;
      }
      entry.target = Math.max(0, entry.target + delta);
      this.pending.set(id, entry);
      this.focus = document.activeElement === link ? { id, step: link.dataset.quantityStep, at: Date.now() } : null;
      this.show(row, entry.target);
      clearTimeout(entry.timer);
      entry.timer = setTimeout(() => this.send(id), entry.target === 0 ? 0 : 300);
    },

    send(id) {
      const entry = this.pending.get(id);
      if (!entry) return;
      clearTimeout(entry.timer);
      entry.timer = null;
      withCartLock(() => this.flush(id));
    },

    sendAll() {
      this.pending.forEach((entry, id) => {
        if (entry.timer) this.send(id);
      });
    },

    queued() {
      return Array.from(this.pending.values()).some((entry) => entry.timer);
    },

    plan(cart, entry, target) {
      const members = cart.items.filter(
        (item) => String(item.variant_id) === String(entry.variantId) && sameProperties(item.properties, entry.properties)
      );
      const current = members.reduce((total, item) => total + item.quantity, 0);
      const updates = {};
      if (target > current && members.length) {
        updates[members[0].key] = members[0].quantity + target - current;
      } else if (target < current) {
        let excess = current - target;
        members
          .slice()
          .reverse()
          .forEach((item) => {
            const removed = Math.min(item.quantity, excess);
            if (!removed) return;
            updates[item.key] = item.quantity - removed;
            excess -= removed;
          });
      }
      return { members, current, updates };
    },

    leave() {
      if (!latestCart || !Array.isArray(latestCart.items)) return;
      const updates = {};
      this.pending.forEach((entry, id) => {
        if (!entry.timer) return;
        clearTimeout(entry.timer);
        Object.assign(updates, this.plan(latestCart, entry, entry.target).updates);
        this.pending.delete(id);
      });
      if (!Object.keys(updates).length) return;
      fetch(`${routes().cartUrl}/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates }),
      }).catch(() => {});
    },

    show(row, target) {
      row.dataset.groupQuantity = String(target);
      const value = row.querySelector('[data-count-value]');
      const label = row.querySelector('[data-count-label]');
      if (value) value.textContent = target;
      if (label) label.textContent = label.textContent.replace(/\d+/, target);
      const less = row.querySelector('[data-quantity-step="-1"]');
      if (less && target > 0) {
        less.innerHTML = icon(target > 1 ? 'minus' : 'trash');
        less.setAttribute('aria-label', target > 1 ? strings().decrease : strings().remove);
      }
      const wrapper = row.closest('.mini-cart-line-wrapper');
      if (target > 0 || !wrapper) return;
      wrapper.hidden = true;
    },

    restore(drawer) {
      const rows = Array.from(drawer.querySelectorAll('line-item[data-group-quantity]'));
      this.pending.forEach((entry, id) => {
        const row = rows.find((element) => rowId(element) === id);
        if (row && Number(row.dataset.groupQuantity) !== entry.target) this.show(row, entry.target);
      });
      const focus = this.focus;
      if (!focus || Date.now() - focus.at > 4000 || drawer.contains(document.activeElement)) return;
      rows
        .find((element) => rowId(element) === focus.id)
        ?.querySelector(`[data-quantity-step="${focus.step}"]`)
        ?.focus({ preventScroll: true });
    },

    async flush(id, retry = false) {
      const entry = this.pending.get(id);
      if (!entry) return;
      const target = entry.target;
      try {
        const cart = await fetchCart();
        const { members, current, updates } = this.plan(cart, entry, target);
        let updatedCart = null;
        if (target > current && members.length) {
          updatedCart = await changeLine(members[0].key, updates[members[0].key]);
        } else if (target < current) {
          updatedCart = await postJson(`${routes().cartUrl}/update.js`, { updates, sections: [CART_SECTION] });
        }
        if (this.pending.get(id) === entry && entry.target === target) this.pending.delete(id);
        if (!updatedCart) {
          if (!members.length) dispatchCart(cart, { replacementDelay: 0 });
          return;
        }
        const rule = readState().gift;
        const handled = rule && gift.action(updatedCart, rule) ? await gift.reconcile(rule).catch(() => false) : false;
        if (!handled) dispatchCart(updatedCart, { replacementDelay: 0 });
      } catch (error) {
        const status = Number(error.json?.status || 0);
        if (!retry && (!status || status === 400)) return this.flush(id, true);
        this.pending.delete(id);
        this.failures += 1;
        toast(error.json?.description || strings().loadError);
        trackCartWork(fetchCart().then((cart) => dispatchCart(cart, { replacementDelay: 0 }), () => {}));
      }
    },
  };

  const onQuantityClick = (event) => {
    const link = event.target.closest('#mini-cart [data-quantity-step]');
    if (!link) return;
    event.preventDefault();
    event.stopPropagation();
    analytics.lineChange(link);
    quantity.step(link);
  };

  const onQuantityKeydown = (event) => {
    if (event.key !== ' ' || !event.target.matches?.('#mini-cart [data-quantity-step]')) return;
    event.preventDefault();
    event.target.click();
  };

  const settleCart = async () => {
    do {
      quantity.sendAll();
      await Promise.all(cartWork);
      await new Promise((resolve) => setTimeout(resolve, 0));
    } while (cartWork.size || quantity.queued());
  };

  const checkout = {
    busy: false,

    waiting() {
      return quantity.pending.size > 0 || cartWork.size > 0;
    },

    mark(busy) {
      const control = getDrawer()?.querySelector('[data-mini-cart-checkout]');
      if (!control) return;
      if (busy) control.setAttribute('aria-busy', 'true');
      else control.removeAttribute('aria-busy');
    },

    async proceed(href) {
      this.busy = true;
      this.mark(true);
      const failures = quantity.failures;
      const settled = await settleCart().then(
        () => quantity.failures === failures,
        () => false
      );
      if (!settled) {
        this.busy = false;
        this.mark(false);
        return;
      }
      if (href) {
        window.location.assign(href);
        return;
      }
      const form = document.createElement('form');
      form.method = 'post';
      form.action = routes().cartUrl;
      form.hidden = true;
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'checkout';
      form.appendChild(field);
      document.body.appendChild(form);
      form.submit();
    },
  };

  const onCheckoutClick = (event) => {
    const control = event.target.closest('#mini-cart [data-mini-cart-checkout]');
    if (!control || (!checkout.busy && !checkout.waiting())) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!checkout.busy) checkout.proceed(control.matches('a[href]') ? control.href : null);
  };

  const onPageHide = () => {
    checkout.busy = false;
    checkout.mark(false);
    quantity.leave();
  };

  const coupon = {
    open: false,
    draft: '',
    error: '',
    logEndpoint:
      'https://script.google.com/macros/s/AKfycbz9JxwpcMHND7_UdqGQINvEsoM7kWc8UGXGcDSW9agzbEIt_RzQBO-VJG3-KdZCyjru8Q/exec',

    root() {
      return getDrawer()?.querySelector('[data-coupon-root]');
    },

    codes() {
      return [...(getDrawer()?.querySelectorAll('[data-code]') || [])].map((element) => element.getAttribute('data-code')).filter(Boolean);
    },

    render(focus) {
      const root = this.root();
      if (!root) return;
      const form = root.querySelector('[data-coupon-form]');
      const toggle = root.querySelector('[data-coupon-toggle]');
      const input = root.querySelector('[data-coupon-input]');
      const error = root.querySelector('[data-coupon-error]');
      root.toggleAttribute('data-open', this.open);
      form.hidden = !this.open;
      toggle.setAttribute('aria-expanded', String(this.open));
      if (!toggle.classList.contains('coupon__toggle--active') && window.SecretSaleSession?.isActive?.()) {
        toggle.classList.add('coupon__toggle--active');
        toggle.innerHTML = `<span class="coupon__chip">${icon('check')}<span class="coupon__label">${escapeHtml(strings().secretSaleActive)}</span></span><span class="visually-hidden">${escapeHtml(strings().couponAddAnother)}</span>`;
      }
      if (input && input.value !== this.draft) input.value = this.draft;
      error.textContent = this.error;
      error.hidden = !this.error;
      if (focus && this.open && input) {
        input.focus({ preventScroll: true });
        input.setSelectionRange(input.value.length, input.value.length);
      }
    },

    log(code, items, success) {
      const body = JSON.stringify({
        code,
        items: (items || []).map((item) => `${item.title} x ${item.quantity}`).join('\n'),
        success,
      });
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(this.logEndpoint, new Blob([body], { type: 'text/plain' }));
          return;
        }
        fetch(this.logEndpoint, { method: 'POST', body, headers: { 'Content-Type': 'text/plain' }, mode: 'no-cors', keepalive: true }).catch(() => {});
      } catch (error) {}
    },

    applications(cart) {
      return [
        ...(cart?.cart_level_discount_applications || []),
        ...(cart?.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
      ].filter(Boolean);
    },

    appliedAmount(cart, code) {
      const wanted = code.toLowerCase();
      const matches = (entry) => String(entry?.title || '').toLowerCase() === wanted;
      const application = this.applications(cart).find(matches);
      if (application && application.value_type === 'percentage') {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'percent', maximumFractionDigits: 0 }).format(
          Number(application.value) / 100
        );
      }
      const orderTotal = (cart?.cart_level_discount_applications || []).filter(matches).reduce((sum, entry) => sum + (entry.total_allocated_amount || 0), 0);
      const lineTotal = (cart?.items || [])
        .flatMap((item) => item.line_level_discount_allocations || [])
        .filter((allocation) => matches(allocation.discount_application))
        .reduce((sum, allocation) => sum + (allocation.amount || 0), 0);
      return money(orderTotal + lineTotal || cart.total_discount || 0);
    },

    async apply() {
      if (this.applying) return;
      const root = this.root();
      const button = root?.querySelector('[data-coupon-apply]');
      const code = this.draft.trim();
      if (!code) {
        this.error = strings().couponEmpty;
        this.render(true);
        shake(root?.querySelector('[data-coupon-form]'));
        return;
      }
      this.error = '';
      this.render();
      this.applying = true;
      button?.setAttribute('aria-busy', 'true');
      try {
        const cart = await window.updateCartDiscountCodes(Array.from(new Set([...this.codes(), code])).join(','));
        const match = (cart?.discount_codes || []).find((entry) => (entry.code || '').toLowerCase() === code.toLowerCase());
        if (match && match.applicable) {
          this.log(code, cart?.items, true);
          this.open = false;
          this.draft = '';
          dispatchCart(cart);
          toast(fill(strings().toastCouponApplied, { amount: this.appliedAmount(cart, match.code) }));
          return;
        }
        this.log(code, cart?.items, false);
        const applicableCodes = (cart?.discount_codes || []).filter((entry) => entry.applicable && entry.code).map((entry) => entry.code);
        const activated = await window.SecretSale?.tryActivate(code, {
          onRecognized: () => toast(strings().secretSaleRecognized),
          afterActivate: () => window.updateCartDiscountCodes(applicableCodes.join(',')),
          applyDiscountCode: async (discountCode) => {
            const merged = Array.from(new Set([...applicableCodes, discountCode]));
            const updatedCart = await window.updateCartDiscountCodes(merged.join(','));
            const discountMatch = (updatedCart?.discount_codes || []).find(
              (entry) => (entry.code || '').toLowerCase() === discountCode.toLowerCase()
            );
            this.log(discountCode, updatedCart?.items, Boolean(discountMatch && discountMatch.applicable));
            this.open = false;
            this.draft = '';
            dispatchCart(updatedCart);
            toast(
              discountMatch && discountMatch.applicable
                ? fill(strings().toastCouponApplied, { amount: this.appliedAmount(updatedCart, discountMatch.code) })
                : strings().secretSaleRecognized
            );
          },
        });
        if (activated) {
          this.open = false;
          this.draft = '';
          this.render();
          return;
        }
        const verdict = await window.DiscountVerify?.verify?.(code).catch(() => null);
        const wanted = code.toLowerCase();
        if (verdict && !verdict.discount) {
          this.error = strings().couponUnknown;
        } else if (verdict && verdict.applies === false && typeof verdict.message === 'string' && verdict.message) {
          this.error = verdict.message;
        } else if (verdict && this.applications(cart).some((entry) => entry.title && String(entry.title).toLowerCase() !== wanted)) {
          this.error = strings().couponNotCombinable;
        } else {
          this.error = strings().couponNotApplicable;
        }
        this.render(true);
        shake(root?.querySelector('[data-coupon-form]'));
      } catch (error) {
        this.log(code, [], false);
        this.error = strings().couponError;
        this.render(true);
      } finally {
        this.applying = false;
        this.root()?.querySelector('[data-coupon-apply]')?.removeAttribute('aria-busy');
      }
    },

    async remove(codeToRemove) {
      try {
        const remaining = this.codes().filter((code) => code.toLowerCase() !== codeToRemove.toLowerCase());
        const cart = await window.updateCartDiscountCodes(remaining.join(','));
        const session = window.SecretSaleSession?.readSession?.();
        if (session?.discountCode && session.discountCode.toLowerCase() === codeToRemove.toLowerCase()) {
          if (window.SecretSaleSession.clearCartOffer) await window.SecretSaleSession.clearCartOffer();
          localStorage.removeItem(window.SecretSaleSession.STORAGE_KEY);
          location.reload();
          return;
        }
        dispatchCart(cart);
      } catch (error) {
        toast(strings().couponError);
      }
    },
  };

  const onCouponClick = (event) => {
    if (event.target.closest('[data-coupon-toggle]')) {
      coupon.open = true;
      coupon.error = '';
      coupon.render(true);
      return;
    }
    if (event.target.closest('[data-coupon-close]')) {
      coupon.open = false;
      coupon.error = '';
      coupon.render();
      coupon.root()?.querySelector('[data-coupon-toggle]')?.focus({ preventScroll: true });
      return;
    }
    const remove = event.target.closest('[data-coupon-remove]');
    if (remove) {
      event.preventDefault();
      trackCartWork(coupon.remove(remove.getAttribute('data-coupon-remove')));
    }
  };

  const onCouponInput = (event) => {
    if (!event.target.matches('[data-coupon-input]')) return;
    coupon.draft = event.target.value;
    if (coupon.error) {
      coupon.error = '';
      coupon.render();
    }
  };

  const onCouponSubmit = (event) => {
    if (!event.target.matches('[data-coupon-form]')) return;
    event.preventDefault();
    event.stopPropagation();
    coupon.draft = event.target.querySelector('[data-coupon-input]')?.value || '';
    trackCartWork(coupon.apply());
  };

  const enableDragClose = (handleSelector, getPanel, close) => {
    let drag = null;
    document.addEventListener('pointerdown', (event) => {
      const handle = event.target.closest(handleSelector);
      if (!handle || event.button !== 0 || event.target.closest('button, a, input')) return;
      if (!window.matchMedia('screen and (max-width: 740px)').matches) return;
      const panel = getPanel(handle);
      if (!panel) return;
      drag = { panel, start: event.clientY, last: event.clientY, time: performance.now(), velocity: 0, id: event.pointerId };
      panel.style.transition = 'none';
    });
    window.addEventListener(
      'pointermove',
      (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const now = performance.now();
        drag.velocity = (event.clientY - drag.last) / Math.max(1, now - drag.time);
        drag.last = event.clientY;
        drag.time = now;
        drag.panel.style.transform = `translateY(${Math.max(0, event.clientY - drag.start)}px)`;
      },
      { passive: true }
    );
    const end = () => {
      if (!drag) return;
      const { panel, start, last, velocity } = drag;
      drag = null;
      panel.style.transition = '';
      panel.style.transform = '';
      const distance = last - start;
      if (distance > panel.offsetHeight * 0.25 || (distance > 20 && velocity > 0.5)) close(panel);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  const keepDrawerAboveKeyboard = () => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    let fitted = false;
    const fit = () => {
      const drawer = getDrawer();
      if (!drawer) return;
      const field = document.activeElement;
      const typing = Boolean(drawer.open && field && drawer.contains(field) && field.matches('input, textarea'));
      if (typing) {
        const covered = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
        drawer.style.setProperty('--mini-cart-viewport-top', `${viewport.offsetTop}px`);
        drawer.style.setProperty('--mini-cart-viewport-height', `${viewport.height}px`);
        drawer.style.setProperty('--mini-cart-viewport-bottom', `${covered}px`);
      } else if (fitted) {
        ['top', 'height', 'bottom'].forEach((edge) => drawer.style.removeProperty(`--mini-cart-viewport-${edge}`));
      }
      fitted = typing;
    };
    viewport.addEventListener('resize', fit);
    viewport.addEventListener('scroll', fit, { passive: true });
    document.addEventListener('focusin', fit);
    document.addEventListener('focusout', () => setTimeout(fit, 0));
  };

  const onSheetOpenerClick = (event) => {
    const opener = event.target.closest('[data-sheet-open]');
    if (!opener) return;
    event.preventDefault();
    getSheet()?.show({
      handle: opener.dataset.handle,
      variantId: opener.dataset.variantId,
      mode: opener.dataset.sheetMode || 'product',
      lineKey: opener.dataset.lineKey || null,
      trigger: opener,
    });
  };

  const onDrawerRendered = (event) => {
    const drawer = event.target;
    const detail = event.detail || {};
    if (detail.cart) knownItemCount = detail.cart.item_count;
    if (knownItemCount === null) knownItemCount = readState().itemCount ?? null;
    coupon.render();
    quantity.restore(drawer);
    if (checkout.busy) checkout.mark(true);
    if (returnFocus && Date.now() - returnFocus.at < 5000 && !getSheet()?.open) {
      const target = drawer.querySelector(returnFocus.selector);
      if (target) {
        target.focus({ preventScroll: true });
        returnFocus = null;
      }
    }
    if (drawer.open && !drawer.contains(document.activeElement) && !getSheet()?.open) {
      drawer.querySelector('.mini-cart__close')?.focus({ preventScroll: true });
    }
    applyCardPrices(drawer);
    if (drawer.open || saleRow.cards) saleRow.fill(drawer);
    if (pendingAdd && Date.now() - pendingAdd.at < 15000) {
      const add = pendingAdd;
      const lines = add.variantIds
        .map((variantId) => drawer.querySelector(`.mini-cart-line[data-variant-id="${variantId}"]`))
        .filter(Boolean);
      if (lines.length) {
        pendingAdd = null;
        const flies = detail.openMiniCart && add.source && (add.sourceRect || add.source.isConnected);
        lines.forEach((line, index) => {
          const last = index === lines.length - 1;
          if (flies) fly(add, line, index, last);
          else land(line, add, (drawer.open ? 60 : 520) + index * 80, last, index === 0);
        });
      }
    }
    if (detail.cart) gift.sync(detail.cart);
  };

  const onDrawerOpened = (event) => {
    event.target.querySelector('mini-cart-goals')?.play(0);
    coupon.render();
    saleRow.fill(event.target);
    trackCartWork(
      fetchCart()
        .then((cart) => {
          analytics.viewCart(cart);
          return gift.sync(cart);
        })
        .catch(() => false)
    );
  };

  let iconCache = null;
  const icon = (name) => {
    if (!iconCache) {
      iconCache = {};
      document
        .querySelector('template[data-mini-cart-icons]')
        ?.content.querySelectorAll('[data-icon]')
        .forEach((element) => {
          iconCache[element.dataset.icon] = element.innerHTML;
        });
    }
    return iconCache[name] || '';
  };

  const normalizeKey = (value) =>
    String(value ?? '')
      .toLowerCase()
      .replace(/\u00df/g, 'ss')
      .replace(/\u00e4/g, 'ae')
      .replace(/\u00f6/g, 'oe')
      .replace(/\u00fc/g, 'ue')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const sheetCache = new Map();

  customElements.whenDefined('drawer-content').then(() => {
    if (customElements.get('mini-cart-sheet')) return;
    const DrawerContent = customElements.get('drawer-content');

    class MiniCartSheet extends DrawerContent {
      connectedCallback() {
        super.connectedCallback();
        this.contentElement = this.querySelector('[data-sheet-content]');
        this.footElement = this.querySelector('[data-sheet-foot]');
        this.openTabs = new Set();
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('scroll', (event) => this.onScroll(event), true);
        this.addEventListener('openable-element:close', () => this.querySelectorAll('video').forEach((video) => video.pause()));
      }

      async show(options) {
        this.options = options;
        this.cartState = readState();
        this.openTabs = new Set();
        this.contentElement.innerHTML = this.skeletonHtml(options.mode);
        this.footElement.innerHTML = '<span class="mini-cart-sheet__skeleton mini-cart-sheet__skeleton--cta" aria-hidden="true"></span>';
        this.contentElement.setAttribute('aria-busy', 'true');
        this.open = true;
        try {
          await this.loadProduct(options.handle, options.variantId);
        } catch (error) {
          if (this.options !== options) return;
          this.contentElement.innerHTML = `${this.headHtml('')}<p class="mini-cart-sheet__error">${escapeHtml(strings().loadError)}</p>`;
          this.footElement.innerHTML = '';
        } finally {
          if (this.options === options) this.contentElement.removeAttribute('aria-busy');
        }
      }

      skeletonHtml(mode) {
        const bar = (kind) => `<span class="mini-cart-sheet__skeleton mini-cart-sheet__skeleton--${kind}"></span>`;
        const choice = `${bar('label')}${bar('swatches')}${mode === 'gift' ? '' : `${bar('label')}${bar('sizes')}`}`;
        return `${this.headHtml('', true)}
          <div class="mini-cart-sheet__loading" aria-hidden="true">
            <div class="mini-cart-sheet__gallery"><div class="mini-cart-sheet__track">${bar('media')}${bar('media')}</div></div>
            <div class="mini-cart-sheet__info">${bar('text')}${bar('text-short')}${bar('price')}</div>
            ${choice}${bar('tabs')}
          </div>`;
      }

      async fetchProduct(handle) {
        const url = `${routes().rootUrlWithoutSlash || ''}/products/${encodeURIComponent(handle)}?section_id=mini-cart-sheet`;
        if (!sheetCache.has(url)) {
          sheetCache.set(
            url,
            fetch(url, { credentials: 'same-origin' }).then((response) => {
              if (!response.ok) throw new Error('Sheet request failed');
              return response.text();
            })
          );
        }
        let html;
        try {
          html = await sheetCache.get(url);
        } catch (error) {
          sheetCache.delete(url);
          throw error;
        }
        const holder = document.createElement('div');
        holder.innerHTML = html;
        const data = parseJson(holder.querySelector('[data-sheet-data]'), null);
        if (!data) throw new Error('Sheet data missing');
        data.tabsHtml = holder.querySelector('template[data-sheet-tabs]')?.innerHTML || '';
        decodeStrings(data.strings);
        data.line = cleanLine(decodeEntities(data.line || ''));
        data.models = (data.models || []).slice().sort((a, b) => String(a.label).localeCompare(String(b.label)));
        return data;
      }

      async loadProduct(handle, variantId, keepColor) {
        const token = (this.loadToken = (this.loadToken || 0) + 1);
        const data = await this.fetchProduct(handle);
        if (token !== this.loadToken) return;
        if (data.extraOptions && this.options.mode !== 'gift' && data.url) {
          window.location.href = data.url;
          return;
        }
        this.data = data;
        const variant = data.variants.find((entry) => String(entry.id) === String(variantId));
        let color = variant ? this.colorOf(variant) : null;
        if (keepColor && data.colors.some((entry) => normalizeKey(entry.name) === normalizeKey(keepColor))) {
          color = data.colors.find((entry) => normalizeKey(entry.name) === normalizeKey(keepColor)).name;
        }
        if (!color || !this.colorAvailable(color)) {
          const firstAvailable = data.variants.find((entry) => entry.a);
          color = firstAvailable ? this.colorOf(firstAvailable) : data.colors[0]?.name || null;
        }
        this.selection = { color, size: null };
        if (this.options.mode === 'gift') this.selection.size = null;
        else this.selection.size = this.guessSize(color);
        this.render(true);
      }

      colorOf(variant) {
        return this.data.colorKey ? variant[this.data.colorKey] : null;
      }

      sizeOf(variant) {
        return this.data.sizeKey ? variant[this.data.sizeKey] : null;
      }

      variantsOfColor(color) {
        return this.data.variants.filter((variant) => !this.data.colorKey || this.colorOf(variant) === color);
      }

      variantFor(color, size) {
        return this.data.variants.find(
          (variant) => (!this.data.colorKey || this.colorOf(variant) === color) && (!this.data.sizeKey || this.sizeOf(variant) === size)
        );
      }

      colorAvailable(color) {
        return this.variantsOfColor(color).some((variant) => variant.a);
      }

      guessSize(color) {
        const wanted = String(this.cartState.shoeSize || '').trim();
        if (!wanted || !this.data.sizeKey) return null;
        const size = this.data.sizes.find((entry) => entry === wanted || entry.split('/').some((part) => part.trim() === wanted));
        const variant = size ? this.variantFor(color, size) : null;
        return variant && variant.a ? size : null;
      }

      priceInfo() {
        const available = this.variantsOfColor(this.selection.color).filter((variant) => variant.a);
        let selected = null;
        if (this.data.sizeKey) {
          if (this.selection.size) selected = this.variantFor(this.selection.color, this.selection.size);
        } else {
          selected = available[0] || this.variantsOfColor(this.selection.color)[0];
        }
        if (selected) return { price: selected.p, compare: selected.c || 0, from: false, variant: selected };
        const prices = available.map((variant) => variant.p);
        if (!prices.length) return { price: 0, compare: 0, from: false, variant: null };
        const price = Math.min(...prices);
        const varies = new Set(prices).size > 1;
        return { price, compare: varies ? 0 : available[0].c || 0, from: varies, variant: null };
      }

      adjustedPrice(info) {
        const secretActive = Boolean(window.SecretSaleSession?.isActive?.());
        if (secretActive && info.variant) {
          const secret = secretPrice(info.variant.s);
          if (secret !== null && secret < info.price) return { ...info, compare: Math.max(info.compare, info.price), price: secret };
        }
        const influencer = !secretActive && window.InfluencerPromo?.state;
        if (influencer && influencer.percent && info.price > 0) {
          return { ...info, compare: Math.max(info.compare, info.price), price: window.InfluencerPromo.cut(info.price, influencer.percent) };
        }
        return info;
      }

      productOff() {
        const offs = this.data.variants
          .filter((variant) => variant.a && variant.c > variant.p)
          .map((variant) => Math.round((1 - variant.p / variant.c) * 100));
        if (!offs.length) return { max: 0, same: true };
        const max = Math.max(...offs);
        return { max, same: offs.every((off) => off === max) && offs.length === this.data.variants.filter((variant) => variant.a).length };
      }

      family() {
        const families = this.cartState.families || [];
        const handle = this.data.handle;
        return families.find((entry) => entry.women === handle || entry.men === handle || entry.kids === handle) || null;
      }

      audienceFamily() {
        const mode = this.options.mode;
        if (mode !== 'product' && mode !== 'upsell') return null;
        const family = this.family();
        if (!family || (mode === 'product' && family.upsell)) return null;
        return ['women', 'men', 'kids'].filter((key) => family[key]).length > 1 ? family : null;
      }

      productTitle() {
        return this.options.mode === 'upsell' || this.audienceFamily() ? this.displayTitle() : this.data.title;
      }

      headlineHtml(title) {
        const headline = String(this.data.headline || '').trim();
        if (!headline || headline === this.data.title) return '';
        const lead = headline.slice(0, headline.length - title.length).trim();
        const splits = lead && headline.toLowerCase().endsWith(` ${title.toLowerCase()}`);
        const text = splits ? `${escapeHtml(lead)}<br>${escapeHtml(headline.slice(headline.length - title.length))}` : escapeHtml(headline);
        return `<span class="mini-cart-sheet__heading">${text}</span>`;
      }

      headHtml(title, loading = false) {
        const s = this.data?.strings || {};
        const heading = loading
          ? '<span class="mini-cart-sheet__skeleton mini-cart-sheet__skeleton--title" aria-hidden="true"></span>'
          : `<h3 class="mini-cart-sheet__title">${escapeHtml(title)}</h3>`;
        return `<div class="mini-cart-sheet__head">
          ${heading}
          <button type="button" class="mini-cart__close" data-action="close" data-sheet-close-button>
            <span class="visually-hidden">${escapeHtml(s.close || strings().sheetClose || '')}</span>${icon('close')}
          </button>
        </div>`;
      }

      displayTitle() {
        const d = this.data;
        const words = d.title.split(' ');
        const suffixes = ['women', 'men', 'kids', 'damen', 'herren', 'kinder', ...Object.values(d.strings.audiences || {}).map((label) => String(label).toLowerCase())];
        return d.audience && words.length > 1 && suffixes.includes(words[words.length - 1].toLowerCase()) ? words.slice(0, -1).join(' ') : d.title;
      }

      galleryHtml() {
        const d = this.data;
        const key = normalizeKey(this.selection.color);
        const variant = this.variantFor(this.selection.color, this.selection.size) || this.variantsOfColor(this.selection.color)[0] || null;
        const featured = variant ? variant.fm : null;
        const visible = d.media.filter((item) => item.u && (!item.k || item.k === key || item.id === featured));
        const mainVideo =
          visible.find((item) => item.video && item.m && item.k === key) || visible.find((item) => item.video && item.m && !item.k);
        const selected = mainVideo || visible.find((item) => item.id === featured) || visible[0];
        let items = selected ? [selected, ...visible.filter((item) => item !== selected)] : [];
        if (!items.length && variant && variant.img) items = [{ u: variant.img }];
        if (!items.length && d.image) items = [{ u: d.image }];
        const influencers = ((variant && variant.inf) || []).map((id) => (d.influencers || {})[id]).filter(Boolean);
        if (influencers.length && items.length) items = [items[0], ...influencers.map((html) => ({ ugc: html })), ...items.slice(1)];
        const name = this.productTitle();
        const alt = this.selection.color ? `${name} \u2013 ${this.selection.color}` : name;
        const count = items.length;
        const toggle = `<button type="button" class="mini-cart-sheet__video-toggle" data-sheet-video-toggle aria-label="${escapeHtml(d.strings.playVideo)}">${icon('play')}</button>`;
        const slides = items
          .map((item, index) => {
            const label = count > 1 ? `${alt}, ${fill(d.strings.imageOf, { index: index + 1, total: count })}` : alt;
            if (item.ugc) {
              return `<div class="mini-cart-sheet__video mini-cart-sheet__ugc" role="group" aria-label="${escapeHtml(label)}">${item.ugc}${item.ugc.includes('<video') ? toggle : ''}</div>`;
            }
            if (item.video) {
              return `<div class="mini-cart-sheet__video" role="group" aria-label="${escapeHtml(label)}">
                <native-video class="mini-cart-sheet__video-player">${item.video}</native-video>
                ${toggle}
              </div>`;
            }
            return `<img src="${escapeHtml(item.u)}" alt="${escapeHtml(label)}" ${index > 1 ? 'loading="lazy"' : ''} draggable="false">`;
          })
          .join('');
        const navigation =
          count > 1
            ? `<button type="button" class="mini-cart-sheet__nav mini-cart-sheet__nav--prev" data-sheet-nav="-1" hidden><span class="visually-hidden">${escapeHtml(d.strings.previousImage)}</span>${icon('chevron')}</button>
               <button type="button" class="mini-cart-sheet__nav mini-cart-sheet__nav--next" data-sheet-nav="1"><span class="visually-hidden">${escapeHtml(d.strings.nextImage)}</span>${icon('chevron')}</button>
               <span class="mini-cart-sheet__counter" aria-hidden="true">1 / ${count}</span>`
            : '';
        return `<div class="mini-cart-sheet__track${count > 1 ? '' : ' mini-cart-sheet__track--single'}" data-sheet-track data-drag-scroll>${slides}</div>${navigation}`;
      }

      watchVideos() {
        this.videoObserver?.disconnect();
        const track = this.contentElement.querySelector('[data-sheet-track]');
        const videos = track ? Array.from(track.querySelectorAll('video')) : [];
        videos.forEach((video) => {
          video.controls = false;
          video.addEventListener('play', () => this.syncVideoToggle(video));
          video.addEventListener('pause', () => this.syncVideoToggle(video));
        });
        if (!videos.length || reduceMotion() || !('IntersectionObserver' in window)) return;
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting && this.open && !video.dataset.userPaused) video.play().catch(() => {});
              else if (!entry.isIntersecting) video.pause();
            });
          },
          { root: track, threshold: 0.6 }
        );
        videos.forEach((video) => this.videoObserver.observe(video));
      }

      syncVideoToggle(video) {
        const button = video.closest('.mini-cart-sheet__video')?.querySelector('[data-sheet-video-toggle]');
        if (!button) return;
        button.innerHTML = icon(video.paused ? 'play' : 'pause');
        button.setAttribute('aria-label', video.paused ? this.data.strings.playVideo : this.data.strings.pauseVideo);
      }

      render(fresh) {
        const d = this.data;
        const s = d.strings;
        const mode = this.options.mode;
        const info = this.adjustedPrice(this.priceInfo());
        const off = this.productOff();
        const state = this.cartState;
        const inSale = mode === 'sale' || (mode === 'product' && Boolean(state.saleRowCollection) && (d.collections || []).includes(state.saleRowCollection));
        const isSale = inSale && (info.compare > info.price || off.max > 0);
        const giftRule = state.gift;
        const family = this.audienceFamily();
        const modelMode = mode === 'product' && Boolean(family);

        let title = this.productTitle();
        let band = '';
        let infoHtml = '';
        const priceHtml = (red) => {
          const compare = info.compare > info.price ? `<s>${money(info.compare)}</s>` : '';
          const current = info.from ? fill(strings().priceFrom, { price: money(info.price) }) : money(info.price);
          const saved = red && info.compare > info.price ? `<em>${fill(escapeHtml(s.youSave), { amount: money(info.compare - info.price) })}</em>` : '';
          return `<div class="mini-cart-sheet__price${red ? ' mini-cart-sheet__price--sale' : ''}">${compare}<span>${current}</span>${saved}</div>`;
        };

        if (mode === 'gift') {
          title = s.giftTitle;
          band = `<p class="mini-cart-sheet__band"><b>${escapeHtml(s.giftBand)}</b><span>${escapeHtml(giftRule ? giftRule.name : d.title)}</span></p>`;
          const regular = info.variant ? info.variant.p : info.price;
          infoHtml = `${d.line ? `<p class="mini-cart-sheet__line">${escapeHtml(d.line)}</p>` : ''}<div class="mini-cart-sheet__price"><s>${money(regular)}</s><span>${money(0)}</span></div>`;
        } else if (mode === 'upsell') {
          const bandText = state.outdoor ? fill(s.upsellBandProduct, { outdoor: state.outdoor }) : s.upsellBand;
          band = `<p class="mini-cart-sheet__band"><b>${escapeHtml(s.upsellLabel)}</b><span>${escapeHtml(bandText)}</span></p>`;
          const heading = state.outdoor ? fill(s.upsellHeadingProduct, { outdoor: escapeHtml(state.outdoor), indoor: escapeHtml(title) }) : s.upsellHeading;
          infoHtml = `<span class="mini-cart-sheet__heading">${heading}</span><p class="mini-cart-sheet__line">${escapeHtml(d.audience === 'kids' ? s.upsellLineKids : s.upsellLine)}</p>${priceHtml(false)}`;
        } else {
          if (isSale) {
            const label = info.compare > info.price ? fill(s.discountLabel, { percent: Math.round((1 - info.price / info.compare) * 100) }) : fill(off.same ? s.discountLabel : s.discountLabelUpTo, { percent: off.max });
            band = `<p class="mini-cart-sheet__band mini-cart-sheet__band--sale"><b>${escapeHtml(label)}</b><span><strong>${escapeHtml(state.saleTitle || s.saleTitle)}</strong>${escapeHtml(s.saleSubtitle)}</span></p>`;
          }
          const headline = modelMode ? this.headlineHtml(title) : '';
          infoHtml = `${headline}${d.line ? `<p class="mini-cart-sheet__line">${escapeHtml(d.line)}</p>` : ''}${priceHtml(isSale)}`;
        }

        let audienceHtml = '';
        if (family) {
          const options = ['women', 'men', 'kids'].filter((key) => family[key]);
          audienceHtml = `<div class="mini-cart-sheet__label"><span>${escapeHtml(s.forWhom)}</span></div>
            <div class="mini-cart-sheet__segment" role="group" aria-label="${escapeHtml(s.forWhom)}" style="--segment-count: ${options.length};">
              ${options
                .map(
                  (key) =>
                    `<button type="button" data-sheet-audience="${escapeHtml(family[key])}" aria-pressed="${family[key] === d.handle}">${escapeHtml(s.audiences[key])}</button>`
                )
                .join('')}
            </div>`;
        }

        let modelsHtml = '';
        if (mode !== 'gift' && d.models.length > 1) {
          modelsHtml = `<div class="mini-cart-sheet__segment mini-cart-sheet__segment--small" role="group" aria-label="${escapeHtml(s.model)}" style="--segment-count: ${d.models.length};">
            ${d.models
              .map(
                (model) =>
                  `<button type="button" data-sheet-model="${escapeHtml(model.handle)}" aria-pressed="${model.handle === d.handle}">${escapeHtml(model.label)}</button>`
              )
              .join('')}
          </div>`;
        }

        const sizesTotal = d.sizes.length;
        const availableSizes = (color) => this.variantsOfColor(color).filter((variant) => variant.a).length;
        const swatchTag = (color) => {
          if (mode === 'gift' && !this.colorAvailable(color.name)) return { text: s.tagSoldOut, kind: 'gone' };
          if (isSale && d.sizeKey && sizesTotal >= 4) {
            const count = availableSizes(color.name);
            if (!count) return { text: s.tagSoldOut, kind: 'gone' };
            if (count <= sizesTotal / 2) return { text: s.tagAlmostGone, kind: 'hot' };
            return null;
          }
          if (color.tag === 'limited') return { text: s.tagLimited, kind: 'limited' };
          if (color.tag === 'bestseller') return { text: s.tagBestseller, kind: '' };
          return null;
        };
        const tags = d.colors.map(swatchTag);
        const swatchesKey = [d.handle, ...tags.map((tag) => (tag ? `${tag.kind}:${tag.text}` : ''))].join('|');
        let colorsHtml = '';
        if (d.colorKey && d.colors.length) {
          const colorCount = (modelMode || mode === 'upsell') && d.colors.length > 1 ? `<span class="mini-cart-sheet__label-end">${escapeHtml(fill(s.colorCountOther, { count: d.colors.length }))}</span>` : '';
          colorsHtml = `<div class="mini-cart-sheet__label"><span>${escapeHtml(s.color)}</span><b>${escapeHtml(this.selection.color || '')}</b>${colorCount}</div>
            <div class="mini-cart-sheet__swatches${tags.some(Boolean) ? ' mini-cart-sheet__swatches--tags' : ''}" data-sheet-swatches="${escapeHtml(swatchesKey)}" data-drag-scroll>
              ${d.colors
                .map((color, index) => {
                  const tag = tags[index];
                  const pressed = color.name === this.selection.color;
                  const label = `${s.color} ${tag ? `${color.name}, ${tag.text}` : color.name}`;
                  return `<button type="button" class="mini-cart-sheet__swatch" data-sheet-color="${escapeHtml(color.name)}" aria-pressed="${pressed}" aria-label="${escapeHtml(label)}">${color.swatch ? `<img src="${escapeHtml(color.swatch)}" alt="" loading="lazy" draggable="false">` : ''}${tag ? `<i class="mini-cart-sheet__tag${tag.kind ? ` mini-cart-sheet__tag--${tag.kind}` : ''}">${escapeHtml(tag.text)}</i>` : ''}</button>`;
                })
                .join('')}
            </div>`;
        }

        let sizesHtml = '';
        if (d.sizeKey && mode !== 'gift') {
          const left = availableSizes(this.selection.color);
          const fomo = isSale && left && left < sizesTotal ? `<span class="mini-cart-sheet__label-end mini-cart-sheet__fomo">${escapeHtml(fill(s.sizesLeft, { count: left, total: sizesTotal }))}</span>` : '';
          const columns = sizesTotal > 8 ? 5 : sizesTotal > 5 ? 4 : Math.max(sizesTotal, 1);
          sizesHtml = `<div class="mini-cart-sheet__label"><span>${escapeHtml(s.size)}</span>${fomo}</div>
            <div class="mini-cart-sheet__sizes${columns === 4 ? ' mini-cart-sheet__sizes--large' : ''}" style="--size-columns: ${columns};" data-sheet-sizes>
              ${d.sizes
                .map((size) => {
                  const variant = this.variantFor(this.selection.color, size);
                  const out = !variant || !variant.a;
                  const low = !out && variant.low ? `<small>${escapeHtml(fill(s.lowStock, { count: variant.low }))}</small>` : '';
                  return `<button type="button" class="mini-cart-sheet__size${low ? ' mini-cart-sheet__size--low' : ''}" data-sheet-size="${escapeHtml(size)}" aria-pressed="${size === this.selection.size}" ${out ? 'disabled' : ''}>${escapeHtml(size)}${low}</button>`;
                })
                .join('')}
            </div>`;
        }

        let hintsHtml = '';
        if (mode !== 'gift' && giftRule && giftRule.threshold > 0) {
          let unlocks = false;
          if (giftRule.type === 'SUBTOTAL') {
            unlocks = state.shopCurrency && state.nonGiftTotal < giftRule.threshold && state.nonGiftTotal + info.price >= giftRule.threshold;
          } else {
            unlocks = state.nonGiftCount < giftRule.threshold && state.nonGiftCount + 1 >= giftRule.threshold;
          }
          if (unlocks) hintsHtml += `<p class="mini-cart-sheet__hint">${icon('gift')}${escapeHtml(fill(s.unlocksGift, { gift: giftRule.name }))}</p>`;
        }
        if (modelMode && d.isShoe) hintsHtml += `<p class="mini-cart-sheet__hint">${icon('check')}${escapeHtml(s.guarantee)}</p>`;

        const tabsHtml = d.tabsHtml ? `<div class="mini-cart-sheet__tabs">${d.tabsHtml}</div>` : '';

        const galleryElement = this.contentElement.querySelector('[data-sheet-gallery]');
        const keepGallery = !fresh && galleryElement && galleryElement.dataset.color === this.selection.color;
        const galleryScroll = keepGallery ? galleryElement.querySelector('[data-sheet-track]')?.scrollLeft || 0 : 0;
        const scrollTop = this.contentElement.scrollTop;
        const swatchesElement = this.contentElement.querySelector('[data-sheet-swatches]');
        const swatchScroll = swatchesElement?.scrollLeft || 0;
        const focused = this.contentElement.contains(document.activeElement) ? document.activeElement : null;
        const focusName = focused
          ? ['data-sheet-color', 'data-sheet-size', 'data-sheet-audience', 'data-sheet-model', 'data-sheet-tab', 'data-sheet-nav', 'data-sheet-close-button'].find((name) =>
              focused.hasAttribute(name)
            )
          : null;
        const focusSelector = focusName ? `[${focusName}="${CSS.escape(focused.getAttribute(focusName))}"]` : null;

        this.contentElement.innerHTML = `${this.headHtml(title)}${band}
          <div class="mini-cart-sheet__gallery" data-sheet-gallery data-color="${escapeHtml(this.selection.color || '')}"></div>
          <div class="mini-cart-sheet__info">${infoHtml}</div>
          ${audienceHtml}${modelsHtml}${colorsHtml}${sizesHtml}${hintsHtml}${tabsHtml}`;

        const gallery = this.contentElement.querySelector('[data-sheet-gallery]');
        if (keepGallery) {
          gallery.replaceWith(galleryElement);
          const keptTrack = galleryElement.querySelector('[data-sheet-track]');
          if (keptTrack) keptTrack.scrollLeft = galleryScroll;
        } else {
          gallery.innerHTML = this.galleryHtml();
          this.watchVideos();
          if (!fresh && !reduceMotion()) gallery.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 220, easing: 'cubic-bezier(.23,1,.32,1)' });
        }
        if (!fresh) this.contentElement.scrollTop = scrollTop;
        this.placeSwatches(fresh ? null : swatchesElement, swatchScroll);

        this.contentElement.querySelectorAll('[data-sheet-tab]').forEach((button) => {
          const open = this.openTabs.has(button.dataset.sheetTab);
          button.setAttribute('aria-expanded', String(open));
          if (button.nextElementSibling) button.nextElementSibling.hidden = !open;
        });
        if (focusSelector) this.contentElement.querySelector(focusSelector)?.focus({ preventScroll: true });

        const ctaLabel = mode === 'gift' ? escapeHtml(s.applyColor) : `<span>${escapeHtml(s.add)}</span><i aria-hidden="true"></i><span>${info.from ? fill(strings().priceFrom, { price: money(info.price) }) : money(info.price)}</span>`;
        this.footElement.innerHTML = `<button type="button" class="mini-cart__checkout mini-cart-sheet__cta${isSale ? ' mini-cart-sheet__cta--sale' : ''}" data-sheet-add>${ctaLabel}</button>`;
      }

      placeSwatches(previous, scrollLeft) {
        let track = this.contentElement.querySelector('[data-sheet-swatches]');
        if (!track) return;
        if (previous) {
          if (previous.dataset.sheetSwatches === track.dataset.sheetSwatches) {
            track.replaceWith(previous);
            track = previous;
            track.querySelectorAll('[data-sheet-color]').forEach((button) => {
              button.setAttribute('aria-pressed', String(button.dataset.sheetColor === this.selection.color));
            });
          }
          track.scrollLeft = scrollLeft;
          return;
        }
        const pressed = track.querySelector('[aria-pressed="true"]');
        if (!pressed) return;
        const start = pressed.getBoundingClientRect().left - track.getBoundingClientRect().left;
        if (start + pressed.offsetWidth > track.clientWidth) track.scrollLeft = start - (track.clientWidth - pressed.offsetWidth) / 2;
      }

      async onClick(event) {
        const target = event.target;
        if (target.closest('[data-sheet-close]')) {
          this.open = false;
          return;
        }
        const color = target.closest('[data-sheet-color]');
        if (color) {
          this.selection.color = color.dataset.sheetColor;
          if (this.selection.size) {
            const variant = this.variantFor(this.selection.color, this.selection.size);
            if (!variant || !variant.a) this.selection.size = null;
          }
          this.render(false);
          return;
        }
        const size = target.closest('[data-sheet-size]');
        if (size && !size.disabled) {
          this.selection.size = size.dataset.sheetSize;
          this.render(false);
          return;
        }
        const switcher = target.closest('[data-sheet-audience], [data-sheet-model]');
        if (switcher) {
          const handle = switcher.dataset.sheetAudience || switcher.dataset.sheetModel;
          if (!handle || handle === this.data.handle) return;
          const keepColor = this.selection.color;
          try {
            await this.loadProduct(handle, null, keepColor);
            if (!reduceMotion()) this.contentElement.querySelector('[data-sheet-gallery]')?.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 220, easing: 'cubic-bezier(.23,1,.32,1)' });
          } catch (error) {
            toast(strings().loadError);
          }
          return;
        }
        const tab = target.closest('[data-sheet-tab]');
        if (tab) {
          const open = tab.getAttribute('aria-expanded') !== 'true';
          tab.setAttribute('aria-expanded', String(open));
          if (tab.nextElementSibling) tab.nextElementSibling.hidden = !open;
          if (open) this.openTabs.add(tab.dataset.sheetTab);
          else this.openTabs.delete(tab.dataset.sheetTab);
          return;
        }
        const videoToggle = target.closest('[data-sheet-video-toggle]');
        if (videoToggle) {
          const video = videoToggle.closest('.mini-cart-sheet__video')?.querySelector('video');
          if (!video) return;
          if (video.paused) {
            delete video.dataset.userPaused;
            video.play().catch(() => {});
          } else {
            video.dataset.userPaused = 'true';
            video.pause();
          }
          return;
        }
        const nav = target.closest('[data-sheet-nav]');
        if (nav) {
          const track = this.contentElement.querySelector('[data-sheet-track]');
          if (!track || !track.firstElementChild) return;
          const images = Array.from(track.children);
          const from = track.navTarget ?? Math.round(track.scrollLeft / (images[0].offsetWidth + 8));
          const to = Math.max(0, Math.min(images.length - 1, from + Number(nav.dataset.sheetNav)));
          track.navTarget = to;
          clearTimeout(track.navTimer);
          track.navTimer = setTimeout(() => (track.navTarget = undefined), 700);
          track.scrollTo({
            left: Math.min(track.scrollWidth - track.clientWidth, images[to].offsetLeft - images[0].offsetLeft),
            behavior: reduceMotion() || document.hidden ? 'auto' : 'smooth',
          });
          return;
        }
        const add = target.closest('[data-sheet-add]');
        if (add) trackCartWork(this.add(add));
      }

      onScroll(event) {
        const track = event.target;
        if (!(track instanceof Element) || !track.matches('[data-sheet-track]') || !track.firstElementChild) return;
        if (track.syncFrame) return;
        track.syncFrame = requestAnimationFrame(() => {
          track.syncFrame = 0;
          const count = track.children.length;
          const step = track.firstElementChild.offsetWidth + 8;
          const max = track.scrollWidth - track.clientWidth;
          const index = track.scrollLeft >= max - 2 ? count - 1 : Math.min(count - 1, Math.round(track.scrollLeft / step));
          const gallery = track.parentElement;
          const counter = gallery.querySelector('.mini-cart-sheet__counter');
          if (counter) counter.textContent = `${index + 1} / ${count}`;
          const buttons = Array.from(gallery.querySelectorAll('[data-sheet-nav]'));
          const focused = buttons.find((button) => button === document.activeElement);
          buttons.forEach((button) => {
            button.hidden = button.dataset.sheetNav === '-1' ? track.scrollLeft < 4 : track.scrollLeft > max - 4;
          });
          if (focused && focused.hidden) buttons.find((button) => !button.hidden)?.focus({ preventScroll: true });
        });
      }

      async add(button) {
        if (this.adding) return;
        const mode = this.options.mode;
        if (this.data.sizeKey && mode !== 'gift' && !this.selection.size) {
          toast(strings().chooseSize);
          shake(this.contentElement.querySelector('[data-sheet-sizes]'));
          return;
        }
        const variant = this.variantFor(this.selection.color, this.selection.size) || (mode === 'gift' ? this.variantsOfColor(this.selection.color).find((entry) => entry.a) : null);
        if (!variant || !variant.a) {
          toast(this.data.strings.tagSoldOut || strings().loadError);
          shake(this.contentElement.querySelector('[data-sheet-color][aria-pressed="true"]'));
          return;
        }
        this.adding = true;
        button.setAttribute('aria-busy', 'true');
        try {
          if (mode === 'gift') {
            const giftRule = this.cartState.gift;
            if (String(variant.id) === String(this.options.variantId)) {
              this.open = false;
              return;
            }
            await withCartLock(async () => {
              const { cart: fresh } = await addItems([{ id: variant.id, quantity: 1, properties: { _free_gift_product: 'true' } }]);
              const previous = fresh.items.find(
                (item) =>
                  String(item.variant_id) === String(this.options.variantId) && item.properties && item.properties._free_gift_product === 'true'
              );
              const cart = previous ? await changeLine(previous.key, 0) : fresh;
              gift.remember(cart, giftRule, variant.id);
              returnFocus = { selector: '[data-gift-line] [data-sheet-open]', at: Date.now() };
              this.open = false;
              gift.dispatch(cart);
            });
            toast(fill(strings().toastGiftColor, { gift: giftRule ? giftRule.name : this.data.title, color: this.selection.color || '' }));
            return;
          }
          const properties = { _upsell_source: 'cart' };
          if (window.SecretSaleSession?.isActive?.()) {
            const session = window.SecretSaleSession.readSession();
            properties._offer = (session && session.offerValue) || 'secret-sale';
          }
          const { added, cart } = await addItems([{ id: variant.id, quantity: 1, properties }]);
          analytics.added(added.items ? added.items[0] : added);
          this.dispatchEvent(
            new CustomEvent('variant:added', { bubbles: true, detail: { variant: added.items ? added.items[0] : added } })
          );
          this.open = false;
          dispatchCart(cart, { replacementDelay: 0 });
        } catch (error) {
          toast(error.json?.description || strings().loadError);
        } finally {
          this.adding = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    customElements.define('mini-cart-sheet', MiniCartSheet);
  });

  const onVariantAdded = (event) => {
    rememberPendingAdd(event);
    prefetchSaleRowAfterAdd();
    analytics.trackKlaviyoAdd(event.detail && event.detail.variant);
  };

  document.addEventListener('pointerdown', rememberAddSource, true);
  document.addEventListener('variant:added', onVariantAdded);
  document.documentElement.addEventListener('influencer:changed', onInfluencerChanged);
  ['pointerover', 'touchstart', 'focusin'].forEach((type) =>
    document.addEventListener(type, prefetchSaleRowOnIntent, { passive: true })
  );
  document.documentElement.addEventListener('cart:updated', onCartUpdated);
  document.addEventListener('discount:updated', onDiscountUpdated);
  document.addEventListener('click', onQuantityClick, true);
  document.addEventListener('keydown', onQuantityKeydown);
  document.addEventListener('click', onCouponClick);
  document.addEventListener('input', onCouponInput);
  document.addEventListener('submit', onCouponSubmit);
  document.addEventListener('click', onCheckoutClick, true);
  window.addEventListener('pagehide', onPageHide);
  enableDragClose(
    '#mini-cart .mini-cart__top',
    () => getDrawer(),
    (panel) => {
      panel.open = false;
    }
  );
  enableDragClose(
    'mini-cart-sheet .mini-cart-sheet__panel > .mini-cart__grab, mini-cart-sheet .mini-cart-sheet__head',
    (handle) => handle.closest('.mini-cart-sheet__panel'),
    () => {
      const sheet = getSheet();
      if (sheet) sheet.open = false;
    }
  );
  keepDrawerAboveKeyboard();
  document.addEventListener('click', onSheetOpenerClick);
  document.addEventListener('cart-drawer:rendered', onDrawerRendered);
  document.addEventListener('cart-drawer:opened', onDrawerOpened);

  if (!customElements.get('mini-cart-toast')) customElements.define('mini-cart-toast', MiniCartToast);
  if (!customElements.get('mini-cart-goals')) customElements.define('mini-cart-goals', MiniCartGoals);

  const firstState = () => {
    const state = readState();
    if (knownItemCount === null && typeof state.itemCount === 'number') knownItemCount = state.itemCount;
    applyCardPrices(getDrawer());
    if (gift.mismatch(state)) gift.refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', firstState, { once: true });
  else firstState();

})();
